import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardActions, CardContent, Grid } from '@mui/material'
import { CardTable } from 'components/datatable/NewTable'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import SimpleTabList from 'components/list/SimpleTabList'
import React, { useEffect, useState } from 'react'
import { COLLECTIVITY, STATUS, YEAR } from 'rpqs/constants/RpqsConstant'
import i18n from 'simple-react-i18n'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import PropTypes from 'prop-types'
import { groupBy } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { push } from '@lagunovsky/redux-react-router'
import { getDate } from 'utils/DateUtil'
import { sortCollectivities } from 'rpqs/utils/RPQSUtils'
import DialogCreateRPQS from './DialogCreateRPQS'
import RpqsAction from 'rpqs/actions/RpqsAction'

const FilterFields = () => {
    return (
        <Card>
            <CardContent>
                <Grid container spacing={4}>
                    <Grid size={3}>
                        <Input
                            tooltip={i18n.searchThenEnter}
                            title={i18n.search}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.collectivity}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.status}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.year}
                        />
                    </Grid>
                </Grid>
            </CardContent>

            <CardActions sx={{ display: 'flex', justifyContent: 'end' }}>
                <Button
                    variant='outlined'
                >
                    {i18n.reinit}
                </Button>
                <Button
                    variant='contained'
                >
                    {i18n.search}
                </Button>
            </CardActions>
        </Card>
    )
}

const TablePanel = ({ rows }) => {
    const {
        collectivities,
    } = useSelector((store) => ({
        collectivities: store.RpqsReducer.collectivities,
    }), shallowEqual)

    const dispatch = useDispatch()

    const rowsFormated = rows.map((collectivity) => {
        return {
            ...collectivity,
            startDate: getDate(collectivity.startDate),
            endDate: getDate(collectivity.endDate),
            collectivity: collectivities.find(c => c.id === collectivity.collectivityId)?.name || i18n.unknown,
        }
    })

    return (
        <CardTable
            title={i18n.collectivity}
            headers={['siren', 'name', 'collectivity', 'startDate', 'endDate', 'status', 'Documentation']}
            rows={rowsFormated}
            onClickRow={(rpqs) => dispatch(push(`/rpqs/${rpqs.year}/collectivite/${rpqs.collectivityId}`))}
        />
    )
}

TablePanel.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.shape({})),
}

const YearPanel = ({ rpqsList }) => {
    const groupedCollectivities = groupBy(rpqsList, ({ year }) => year || i18n.unknown)

    return (
        <>
            {Object.keys(groupedCollectivities).sort((a, b) => sortCollectivities(a, b, true)).map((year, i) => {
                return (
                    <Accordion key={year} defaultExpanded={i === 0} className='margin-bottom-1'>
                        <AccordionSummary>
                            <span>{year} ({groupedCollectivities[year].length} {i18n.folders})</span>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: 0 }}>
                            <TablePanel rows={groupedCollectivities[year]}/>
                        </AccordionDetails>
                    </Accordion>
                )
            })}
        </>
    )
}

YearPanel.propTypes = {
    rpqsList: PropTypes.arrayOf(PropTypes.shape({})),
}

const StatusPanel = ({ rpqsList }) => {
    const groupedCollectivities = groupBy(rpqsList, ({ status }) => status || i18n.unknown)

    return (
        <>
            {Object.keys(groupedCollectivities).sort((a, b) => sortCollectivities(a, b)).map((status, i) => {
                return (
                    <Accordion key={status} defaultExpanded={i === 0} className='margin-bottom-1'>
                        <AccordionSummary>
                            <span>{status} ({groupedCollectivities[status].length} {i18n.folders})</span>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: 0 }}>
                            <TablePanel rows={groupedCollectivities[status]}/>
                        </AccordionDetails>
                    </Accordion>
                )
            })}
        </>
    )
}

StatusPanel.propTypes = {
    rpqsList: PropTypes.arrayOf(PropTypes.shape({})),
}

const CollectivityPanel = ({ rpqsList }) => {
    const {
        collectivities,
    } = useSelector((store) => ({
        collectivities: store.RpqsReducer.collectivities,
    }), shallowEqual)

    const groupedCollectivities = groupBy(rpqsList, ({ collectivityId }) => collectivities.find(c => c.id === collectivityId)?.name || i18n.unknown)

    return (
        <>
            {Object.keys(groupedCollectivities).sort((a, b) => sortCollectivities(a, b)).map((collectivity, i) => {
                return (
                    <Accordion key={collectivity} defaultExpanded={i === 0} className='margin-bottom-1'>
                        <AccordionSummary>
                            <span>{collectivity} ({groupedCollectivities[collectivity].length} {i18n.folders})</span>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: 0 }}>
                            <TablePanel rows={groupedCollectivities[collectivity]}/>
                        </AccordionDetails>
                    </Accordion>
                )
            })}
        </>
    )
}

CollectivityPanel.propTypes = {
    rpqsList: PropTypes.arrayOf(PropTypes.shape({})),
}

const RpqsListDashboardApp = () => {
    const {
        rpqsList,
    } = useSelector((store) => ({
        rpqsList: store.RpqsReducer.rpqsList,
    }), shallowEqual)

    const dispatch = useDispatch()

    const [openCreateRPQS, setOpenCreateRPQS] = useState(false)

    useEffect(() => {
        dispatch(RpqsAction.fetchAllCollectivities())
        dispatch(RpqsAction.fetchAllRpqs())
    }, [])

    useTitle(() => [{
        title: 'RPQS',
        href: 'rpqs',
    }, {
        title: i18n.dashboard,
        href: 'rpqs',
    }], [])

    useActions(() => ({
        new: () => setOpenCreateRPQS(true),
        export: () => {},
    }), [])

    return (
        <>
            <SimpleTabList
                tabs={[
                    {
                        constant: YEAR,
                        label: i18n.year,
                        icon: 'event',
                    },
                    {
                        constant: COLLECTIVITY,
                        label: i18n.collectivity,
                        icon: 'house',
                    },
                    {
                        constant: STATUS,
                        label: i18n.status,
                        icon: 'work',
                    },
                ]}
                headerComponent={<FilterFields/>}
                noPadding
                defaultTab={YEAR}
            >
                {tab => (
                    <Grid sx={{ padding: '10px' }}>
                        {tab === YEAR && (
                            <YearPanel rpqsList={rpqsList}/>
                        )}
                        {tab === COLLECTIVITY && (
                            <CollectivityPanel rpqsList={rpqsList}/>
                        )}
                        {tab === STATUS && (
                            <StatusPanel rpqsList={rpqsList}/>
                        )}
                    </Grid>
                )}
            </SimpleTabList>
            <DialogCreateRPQS
                open={openCreateRPQS}
                onClose={() => setOpenCreateRPQS(false)}
            />
        </>
    )
}

export default RpqsListDashboardApp